import { CompactPagination } from "@/components/Pagination";
import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import {
  ScrollContainer,
  WidgetContainer,
} from "@/features/workspace/widgets/Components";
import {
  type QuotationsWidgetList,
  type WidgetSetting,
  type WidgetSize,
  useQuotationWidgetSearch,
} from "@repo/api";
import { Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { useEffect, useMemo, useState } from "react";

import { CARD_HEIGHT_NORMAL, CARD_HEIGHT_TALL } from "../constants";
import { QuotationsList } from "./QuotationsList";
import { getWidgetSettings, mapQuotationData } from "./utils";

interface QuotationsWidgetProps {
  title: string;
  settings?: WidgetSetting;
  widgetSize: WidgetSize;
}

export const QuotationsWidget = ({
  title,
  settings,
  widgetSize,
}: QuotationsWidgetProps) => {
  const [quotations, setQuotations] = useState<QuotationsWidgetList[]>([]);
  const [pageIndex, setPageIndex] = useState(0);

  const pageSize = useMemo(() => (widgetSize === "M" ? 10 : 5), [widgetSize]);

  const widgetSettings = useMemo(
    () => ({
      ...getWidgetSettings(settings),
      pageSize,
      pageIndex,
    }),
    [settings, pageIndex, pageSize]
  );

  const { data, isValidating } = useQuotationWidgetSearch(widgetSettings);

  useEffect(() => {
    if (data?.content) {
      setQuotations((prevQuotations) =>
        mapQuotationData(prevQuotations, data.content)
      );
    }
  }, [data]);
  const loadMoreQuotations = () => {
    setPageIndex((prev) => prev + 1);
  };

  const totalItems = data?.totalElements ?? 0;

  const widgetHeight =
    widgetSize === "M" ? CARD_HEIGHT_TALL : CARD_HEIGHT_NORMAL;

  return (
    <WidgetContainer size={widgetHeight}>
      <ActionTitle title={`${title} (${quotations.length})`} />

      {!isValidating && quotations.length === 0 ? (
        <Text>{t("prompt.no_quotations")}</Text>
      ) : (
        <>
          <ScrollContainer>
            <QuotationsList quotations={quotations} />
          </ScrollContainer>

          {quotations.length < totalItems && (
            <CompactPagination
              setSize={loadMoreQuotations}
              size={pageSize}
              totalNumber={totalItems}
              currentNumber={quotations.length}
            />
          )}
        </>
      )}
    </WidgetContainer>
  );
};
